import { useEffect, useRef, useId } from 'react';
import Button from '@/components/homepage/Button';
import beTwMerge from '../../../utils/beTwMerge';
import { ColorKeys } from '../../../styles/colors';

const coreCarouselStyle = `
      tw-pb-[44px]
      lg:tw-pb-[68px]
      tw-pt-[24px]
      lg:tw-pt-[48px]
`;

const carouselStyles = {
  'dark-green': beTwMerge(
    coreCarouselStyle,
    `
      tw-text-color-white
      tw-bg-color-dark-green
    `
  ),
  white: beTwMerge(
    coreCarouselStyle,
    `
      tw-text-color-black
      tw-bg-color-white
    `
  ),
};

const ctaStyles = {
  'dark-green': 'white',
  white: 'black',
};

export type DashHudsonCarouselProps = {
  header: string;
  carouselID: string;
  carouselName: string;
  carouselSrc: string;
  cta: {
    label: string;
    link: string;
  };
  carouselContainerClassName?: string;
  className?: string;
  color?: ColorKeys;
  isFull?: boolean;
};

export default function DashHudsonCarousel({
  header,
  cta,
  carouselName,
  carouselSrc,
  isFull,
  color = 'dark-green',
  className,
  carouselContainerClassName,
  carouselID,
}: DashHudsonCarouselProps) {
  const carouselContainerRef = useRef<any>(null);
  const classNames = beTwMerge(
    carouselStyles[color],
    className,
    isFull
      ? 'dash-hudson-carousel is-full'
      : 'dash-hudson-carousel tw-mx-4 lg:tw-mx-8 min-[1504px]:tw-mx-auto min-[1504px]:tw-w-[1440px] tw-max-w-[1440px]'
  );
  const uniqueId = useId().replace(/:/g, '');

  useEffect(() => {
    if (document.querySelector(`#hudson-script-${uniqueId}`)) {
      return;
    }

    const script = document.createElement('script');

    script.src = carouselSrc;
    script.dataset.name = carouselName;
    script.id = `hudson-script-${uniqueId}`;

    script.dataset.galleryId = carouselID;
    script.dataset.id = carouselID;

    script.dataset.idType = 'brand_id';
    script.dataset.autoplay = 'false';
    script.dataset.carouselDots = 'false';
    script.dataset.hidePopupFooter = 'true';
    script.dataset.disableProductNewTab = 'false';
    script.dataset.lazy = 'false';
    script.dataset.rowSize = isFull ? '6' : '4.2';
    script.dataset.gapSize = isFull ? '8' : '16';
    script.dataset.mobileRowSize = '2';
    script.dataset.mobileGapSize = isFull ? '8' : '16';
    script.dataset.callToAction = 'shop_now';
    script.async = false;

    const div = carouselContainerRef.current;

    if (div && div.childElementCount > 0) {
      div.innerHTML = '';
    }

    div.appendChild(script);

    return () => {
      script.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
    Applies minimal changes in carousel to meet SortSite compliance
  */
  useEffect(() => {
    const observer = new MutationObserver(records => {
      records.forEach(record => {
        if (
          record.target instanceof HTMLAnchorElement &&
          !record.target.hasAttribute('href') &&
          record.attributeName === 'tabindex'
        ) {
          // Dummy href for SortSite compliance
          record.target.setAttribute('href', '#');
          observer.disconnect();
        }
      });
    });
    observer.observe(carouselContainerRef.current, { subtree: true, attributeFilter: ['tabindex'] });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className={classNames}>
      <div className="tw-flex tw-max-w-full tw-justify-center md:tw-justify-between md:tw-items-center tw-mb-4 md:tw-mb-5 tw-mx-4 lg:tw-mx-8">
        <h2 className="tw-text-center md:tw-text-left tw-font-ebgaramond">{header}</h2>

        {cta.label && (
          <Button
            className="tw-hidden tw-underline md:tw-block hover:tw-text-color-light-green"
            color={ctaStyles[color]}
            href={cta.link}
            type="link"
            label={cta.label}
          />
        )}
      </div>
      <div className={beTwMerge('tw-overflow-hidden tw-relative', isFull ? '' : 'tw-px-2 md:tw-px-[6px]')}>
        {isFull ? (
          <div className="tw-overflow-hidden">
            <div className="tw-mx-[-8px]">
              <div
                className={beTwMerge('tw-mb-4 tw-static tw-w-full', carouselContainerClassName)}
                ref={carouselContainerRef}
              />
            </div>
          </div>
        ) : (
          <div
            className={beTwMerge('tw-mb-4 tw-static tw-w-full', carouselContainerClassName)}
            ref={carouselContainerRef}
          />
        )}
      </div>
      {cta.label && (
        <div className="tw-flex md:tw-hidden tw-justify-center">
          <Button
            className="hover:tw-text-color-light-green tw-underline"
            href={cta.link}
            type="link"
            color={ctaStyles[color]}
            label={cta.label}
          />
        </div>
      )}
    </div>
  );
}
